// File: src/pages/Ledgers/LedgerReport.jsx
import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "../../axios";
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Divider,
  Container,
  Alert,
  Fade,
  Zoom,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import {
  AccountBalance as SafeIcon,
  AccountBalanceWallet as BankIcon,
  Person as CustomerIcon,
  Business as SupplierIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as ReportIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { SettingsContext } from "../../contexts/SettingsContext";
import { toast } from "react-toastify";
import dayjs from "dayjs";
// إنشاء cache للـ RTL
const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

// إنشاء cache للـ LTR
const ltrCache = createCache({
  key: 'muiltr',
  stylisPlugins: [prefixer],
});

export default function LedgerReport() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const companyIdFromLS = parseInt(localStorage.getItem("companyId") || "0", 10);
  const branchIdFromLS  = parseInt(localStorage.getItem("branchId")  || "0", 10);

  const { decimalPlaces } = useContext(SettingsContext);
  const dp  = decimalPlaces ?? 2;
  
  // تنسيق الأرقام - دائماً بالإنجليزية
  const fmt = (n) => Number(n ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });

  // إنشاء theme مع دعم RTL
  const theme = createTheme({
    direction: isRTL ? 'rtl' : 'ltr',
    typography: {
      fontFamily: isRTL 
        ? '"Cairo", "Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
        : '"Roboto", "Helvetica", "Arial", sans-serif',
    },
  });

  // أنواع التقارير مع الترجمة
  const ledgerTypes = [
    { 
      key: "safe", 
      label: t('ledgerReport.safeMovement', 'حركة الصندوق'), 
      endpoint: "/ledgers/safe-ledger",
      icon: <SafeIcon />,
      color: "#2196F3"
    },
    { 
      key: "bank", 
      label: t('ledgerReport.bankMovement', 'حركة البنك'), 
      endpoint: "/ledgers/bank-ledger",
      icon: <BankIcon />,
      color: "#4CAF50"
    }
  ];

  const [type, setType] = useState("safe");
   
  // نوع التقرير الحالي
  const currentLedgerType = ledgerTypes.find(x => x.key === type);

  // فلاتر - إخفاء فلتر الشركة
  const [filters, setFilters] = useState({
    companyId: companyIdFromLS || 0,
    branchId: branchIdFromLS || "",
    safeId: "", bankId: "", customerId: "", supplierId: "",
    // استخدم تنسيق مناسب لحقل datetime-local ولـ API
    from: dayjs().startOf("month").format("YYYY-MM-DDTHH:mm"),
    to: dayjs().endOf("month").format("YYYY-MM-DDTHH:mm"),
  });

  // لوائح
  const [branches, setBranches]   = useState([]);
  const [safes, setSafes]         = useState([]);
  const [banks, setBanks]         = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  // بيانات التقرير
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState({
    openingBalance: 0, totalIn: 0, totalOut: 0,
    totalDebit: 0, totalCredit: 0, closingBalance: 0,
    items: [],
  });

  // تحميل القوائم عند اختيار الشركة
  useEffect(() => {
    const cid = filters.companyId;
    if (!cid) return;
    axios.get(`/Branch/by-company/${cid}`).then(r => setBranches(r.data)).catch(()=>{});
    axios.get(`/safe/by-company/${cid}`).then(r => setSafes(r.data)).catch(()=>{});
    axios.get(`/bank/by-company/${cid}`).then(r => setBanks(r.data)).catch(()=>{});
    axios.get(`/Customer/by-company/${cid}`).then(r => setCustomers(r.data)).catch(()=>{});
    axios.get(`/Supplier/by-company/${cid}`).then(r => setSuppliers(r.data)).catch(()=>{});
  }, [filters.companyId]);

  // أنواع الفواتير مع أسماء واضحة
  const [typeMap, setTypeMap] = useState({});
  useEffect(() => {
    const cid = Number(filters.companyId);
    if (!cid) return;
    axios.get(`/InvoiceTypes`, { params: { companyId: cid }})
      .then(r => {
        const list = r.data || [];
        const map = {};
        list.forEach(invoiceType => {
          // جرب جميع الاحتمالات للحصول على ID
          const id = invoiceType.invoiceTypeId ?? 
                    invoiceType.InvoiceTypeId ?? 
                    invoiceType.id ?? 
                    invoiceType.Id ?? 
                    invoiceType.code ?? 
                    invoiceType.Code;
          
          // جرب جميع الاحتمالات للحصول على الاسم
          let name = invoiceType.name ?? 
                    invoiceType.Name ?? 
                    invoiceType.nameAr ?? 
                    invoiceType.NameAr ?? 
                    invoiceType.arabicName ?? 
                    invoiceType.ArabicName;
          
          // إذا لم يكن هناك اسم، نقوم بإنشاء اسم وصفي بناءً على النوع
          if (!name && id != null) {
            // أسماء وصفية لأنواع السندات الشائعة - محدثة حسب VouchersController.cs
            const typeNames = {
              1: t('voucherTypes.salesInvoice', 'فاتورة مبيعات'),
              2: t('voucherTypes.purchaseInvoice', 'فاتورة مشتريات'),
              3: t('voucherTypes.returnSales', 'مردود مبيعات'),
              4: t('voucherTypes.returnPurchase', 'مردود مشتريات'),
              5: t('voucherTypes.journalEntry', 'قيد يومية'),
              6: t('voucherTypes.openingBalance', 'رصيد افتتاحي'),
              7: t('voucherTypes.bankTransfer', 'تحويل بنكي'),
              8: t('voucherTypes.safeTransfer', 'تحويل صندوق'),
              // أرقام السندات الجديدة من VouchersController.cs
              101: t('voucherTypes.customerReceipt', 'سند قبض من عميل'),
              102: t('voucherTypes.supplierPayment', 'سند صرف مورد'),
              103: t('voucherTypes.generalReceipt', 'سند قبض عام'),
              104: t('voucherTypes.generalPayment', 'سند صرف عام')
            };
            name = typeNames[id] || `${t('common.type', 'نوع')} ${id}`;
          }
          
          if (id != null) {
            map[id] = name;
            // أضف أيضاً النوع كـ string في حالة كان ID نصي
            map[String(id)] = name;
          }
        });
        setTypeMap(map);
      })
      .catch((err) => {
        console.error('Error loading invoice types:', err);
      });
  }, [filters.companyId, t]);

  // باراميترات API
  const normalizeDateForApi = (v) => {
    if (!v) return undefined;
    const d = dayjs(v);
    return d.isValid() ? d.format("YYYY-MM-DDTHH:mm:ss") : undefined;
  };
  const buildParams = () => {
    const p = { companyId: Number(filters.companyId) };
    if (filters.branchId) p.branchId = Number(filters.branchId);
    // تطبيع التاريخ لإرساله بصيغة متسقة يسهل على .NET قراءتها
    if (filters.from) p.from = normalizeDateForApi(filters.from);
    if (filters.to)   p.to   = normalizeDateForApi(filters.to);
    if (type === "safe"     && filters.safeId)     p.safeId     = Number(filters.safeId);
    if (type === "bank"     && filters.bankId)     p.bankId     = Number(filters.bankId);
    if (type === "customer" && filters.customerId) p.customerId = Number(filters.customerId);
    if (type === "supplier" && filters.supplierId) p.supplierId = Number(filters.supplierId);
    return p;
  };

  const handleFetch = async () => {
    if (!filters.companyId) { 
      toast.error(t('common.companyIdRequired', 'رقم الشركة مطلوب')); 
      return; 
    }
    setLoading(true);
    try {
      const ep = ledgerTypes.find(x => x.key === type).endpoint;
      const res = await axios.get(ep, { params: buildParams() });
      setReport({
        openingBalance: res.data.openingBalance ?? 0,
        totalIn: res.data.totalIn ?? 0,
        totalOut: res.data.totalOut ?? 0,
        totalDebit: res.data.totalDebit ?? 0,
        totalCredit: res.data.totalCredit ?? 0,
        closingBalance: res.data.closingBalance ?? 0,
        items: (res.data.items || []).map(normalizeRow),
      });
    } catch (err) {
      console.error(err);
      toast.error(t('common.failedToLoadReport', 'تعذّر تحميل التقرير'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { handleFetch(); /* eslint-disable-next-line */ }, [type]);

  const onTypeChange = (newType) => {
    setType(newType);
    setReport({ openingBalance:0,totalIn:0,totalOut:0,totalDebit:0,totalCredit:0,closingBalance:0,items:[] });
    setFilters(f => ({ ...f, safeId:"", bankId:"", customerId:"", supplierId:"" }));
  };

  // أعمدة
  const isMoneyLedger = type === "safe" || type === "bank";
  const columns = useMemo(() => (
    isMoneyLedger
      ? [
          { key: "txDate", label: t('ledgerReport.date', 'التاريخ'), width: "15%" },
          { key: "referenceId", label: t('ledgerReport.reference', 'المرجع'), width: "10%" },
          { key: "invoiceNumber", label: t('ledgerReport.invoiceNumber', 'رقم الفاتورة'), width: "12%" },
          { key: "invoiceTypeId", label: t('ledgerReport.type', 'النوع'), width: "15%" },
          { key: "notes", label: t('ledgerReport.notes', 'ملاحظات'), width: "20%" },
          { key: "amountIn", label: t('ledgerReport.incoming', 'وارد'), width: "12%", align: "right" },
          { key: "amountOut", label: t('ledgerReport.outgoing', 'صادر'), width: "12%", align: "right" },
          { key: "runningBalance", label: t('ledgerReport.runningBalance', 'الرصيد المتراكم'), width: "14%", align: "right" },
        ]
      : [
          { key: "txDate", label: t('ledgerReport.date', 'التاريخ'), width: "15%" },
          { key: "referenceId", label: t('ledgerReport.reference', 'المرجع'), width: "10%" },
          { key: "invoiceNumber", label: t('ledgerReport.invoiceNumber', 'رقم الفاتورة'), width: "12%" },
          { key: "invoiceTypeId", label: t('ledgerReport.type', 'النوع'), width: "15%" },
          { key: "notes", label: t('ledgerReport.notes', 'ملاحظات'), width: "20%" },
          { key: "debit", label: t('ledgerReport.debit', 'مدين'), width: "12%", align: "right" },
          { key: "credit", label: t('ledgerReport.credit', 'دائن'), width: "12%", align: "right" },
          { key: "runningBalance", label: t('ledgerReport.runningBalance', 'الرصيد المتراكم'), width: "14%", align: "right" },
        ]
  ), [isMoneyLedger, t]);

  // Normalize row
  function normalizeRow(src) {
    const get = (k1, k2) => (k1 in src ? src[k1] : src[k2]);
    const row = {
      txDate:          get("txDate", "TxDate"),
      referenceId:     get("referenceId", "ReferenceId"),
      invoiceNumber:   get("invoiceNumber", "InvoiceNumber"),
      invoiceTypeId:   get("invoiceTypeId", "InvoiceTypeId") ?? get("type", "Type") ?? get("voucherType", "VoucherType"),
      notes:           get("notes", "Notes"),
      amountIn:   Number(get("amountIn", "AmountIn") ?? 0),
      amountOut:  Number(get("amountOut", "AmountOut") ?? 0),
      debit:      Number(get("debit", "Debit") ?? 0),
      credit:     Number(get("credit", "Credit") ?? 0),
      runningBalance: Number(get("runningBalance", "RunningBalance") ?? 0),
    };
    return row;
  }

  // خلية الجدول
  // تحسين دالة renderCell لعرض أنواع السندات
  function renderCell(key, row) {
    const moneyKeys = ["amountIn","amountOut","debit","credit","runningBalance"];
    if (key === "txDate") {
      // تنسيق التاريخ الأمريكي بالأرقام الإنجليزية دائماً
      return row.txDate ? new Date(row.txDate).toLocaleDateString('en-US') : "";
    }
    if (key === "invoiceTypeId") {
      const typeId = row.invoiceTypeId;
      
      // البحث في typeMap بجميع الطرق الممكنة
      let typeName = typeMap[typeId] || typeMap[String(typeId)];
      
      // إذا لم نجد الاسم، نستخدم الأسماء الافتراضية
      if (!typeName && typeId != null) {
        const defaultTypeNames = {
          1: t('voucherTypes.receiptFromCustomer', 'سند قبض من عميل'),
          2: t('voucherTypes.paymentToSupplier', 'سند صرف لمورد'),
          3: t('voucherTypes.salesInvoice', 'فاتورة مبيعات'),
          4: t('voucherTypes.purchaseInvoice', 'فاتورة مشتريات'),
          5: t('voucherTypes.returnSales', 'مردود مبيعات'),
          6: t('voucherTypes.returnPurchase', 'مردود مشتريات'),
          7: t('voucherTypes.journalEntry', 'قيد يومية'),
          8: t('voucherTypes.openingBalance', 'رصيد افتتاحي'),
          9: t('voucherTypes.bankTransfer', 'تحويل بنكي'),
          10: t('voucherTypes.safeTransfer', 'تحويل صندوق'),
          101: t('voucherTypes.customerReceipt', 'سند قبض من عميل'),
          102: t('voucherTypes.supplierPayment', 'سند صرف لمورد')
        };
        typeName = defaultTypeNames[typeId] || `${t('common.type', 'نوع')} ${typeId}`;
      }
      
      return typeName ? (
        <Chip 
          label={typeName} 
          size="small" 
          variant="outlined" 
          sx={{ maxWidth: '150px' }}
        />
      ) : "";
    }
    if (moneyKeys.includes(key)) {
      const value = row[key];
      const isNegative = value < 0;
      return (
        <Typography 
          variant="body2" 
          sx={{ 
            color: isNegative ? 'error.main' : 'text.primary',
            fontWeight: key === 'runningBalance' ? 'bold' : 'normal'
          }}
        >
          {fmt(value)}
        </Typography>
      );
    }
    return row[key] ?? "";
  }

  return (
    <CacheProvider value={isRTL ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* العنوان الرئيسي */}
        <Fade in timeout={800}>
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h4" component="h1" sx={{ 
              fontWeight: 'bold', 
              color: 'primary.main',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}>
              <ReportIcon sx={{ fontSize: 40 }} />
              {t('ledgerReport.title', 'تقارير الحركة المالية')}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {t('ledgerReport.subtitle', 'عرض وتحليل الحركات المالية للشركة')}
            </Typography>
          </Box>
        </Fade>

        {/* أزرار نوع التقرير */}
        <Zoom in timeout={600}>
          <Card sx={{ mb: 3, overflow: 'visible' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterIcon />
                {t('ledgerReport.reportType', 'نوع التقرير')}
              </Typography>
              <Grid container spacing={2}>
                {ledgerTypes.map((t) => (
                  <Grid item xs={12} sm={6} md={3} key={t.key}>
                    <Button
                      fullWidth
                      variant={type === t.key ? "contained" : "outlined"}
                      size="large"
                      startIcon={t.icon}
                      onClick={() => onTypeChange(t.key)}
                      sx={{
                        py: 2,
                        backgroundColor: type === t.key ? t.color : 'transparent',
                        borderColor: t.color,
                        color: type === t.key ? 'white' : t.color,
                        '&:hover': {
                          backgroundColor: type === t.key ? t.color : `${t.color}15`,
                          borderColor: t.color,
                        },
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {t.label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Zoom>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            {/* فلاتر - بدون فلتر الشركة */}
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon />
              {t('ledgerReport.searchFilters', 'فلاتر البحث')}
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('ledgerReport.branch', 'الفرع')}</InputLabel>
                <Select
                  value={filters.branchId}
                  label={t('ledgerReport.branch', 'الفرع')}
                  onChange={(e) => setFilters(f => ({ ...f, branchId: e.target.value }))}
                >
                  <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
                  {branches.map(b => {
                    const id = b.id ?? b.Id;
                    const name = b.name ?? b.Name;
                    return <MenuItem key={id} value={id}>{name}</MenuItem>;
                  })}
                </Select>
              </FormControl>
              </Grid>

              {type === "safe" && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('ledgerReport.safe', 'الصندوق')}</InputLabel>
                  <Select
                    value={filters.safeId}
                    label={t('ledgerReport.safe', 'الصندوق')}
                    onChange={(e) => setFilters(f => ({ ...f, safeId: e.target.value }))}
                  >
                    <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
                    {safes.map(s => {
                      const id = s.safeId ?? s.SafeId ?? s.id ?? s.Id;
                      const name = s.name ?? s.Name;
                      return <MenuItem key={id} value={id}>{name}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Grid>
              )}

              {type === "bank" && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('ledgerReport.bank', 'البنك')}</InputLabel>
                  <Select
                    value={filters.bankId}
                    label={t('ledgerReport.bank', 'البنك')}
                    onChange={(e) => setFilters(f => ({ ...f, bankId: e.target.value }))}
                  >
                    <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
                    {banks.map(b => {
                      const id = b.id ?? b.Id;
                      const name = b.name ?? b.Name;
                      return <MenuItem key={id} value={id}>{name}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Grid>
              )}

              {type === "customer" && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('ledgerReport.customer', 'العميل')}</InputLabel>
                  <Select
                    value={filters.customerId}
                    label={t('ledgerReport.customer', 'العميل')}
                    onChange={(e) => setFilters(f => ({ ...f, customerId: e.target.value }))}
                  >
                    <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
                    {customers.map(c => {
                      const id = c.id ?? c.Id;
                      const name = c.name ?? c.Name ?? c.arabicName ?? c.ArabicName;
                      return <MenuItem key={id} value={id}>{name}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Grid>
              )}

              {type === "supplier" && (
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('ledgerReport.supplier', 'المورد')}</InputLabel>
                  <Select
                    value={filters.supplierId}
                    label={t('ledgerReport.supplier', 'المورد')}
                    onChange={(e) => setFilters(f => ({ ...f, supplierId: e.target.value }))}
                  >
                    <MenuItem value="">{t('common.all', 'الكل')}</MenuItem>
                    {suppliers.map(s => {
                      const id = s.id ?? s.Id;
                      const name = s.name ?? s.Name ?? s.arabicName ?? s.ArabicName;
                      return <MenuItem key={id} value={id}>{name}</MenuItem>;
                    })}
                  </Select>
                </FormControl>
              </Grid>
              )}

              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label={t('ledgerReport.fromDate', 'من تاريخ')}
                  type="datetime-local"
                  value={filters.from}
                  onChange={(e) => setFilters(f => ({ ...f, from: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label={t('ledgerReport.toDate', 'إلى تاريخ')}
                  type="datetime-local"
                  value={filters.to}
                  onChange={(e) => setFilters(f => ({ ...f, to: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleFetch}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <ReportIcon />}
                  sx={{ height: '40px' }}
                >
                  {loading ? t('common.loading', 'جارِ التحميل...') : t('ledgerReport.showReport', 'عرض التقرير')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ملخص الإحصائيات */}
        <Fade in timeout={1000}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                {currentLedgerType?.icon}
                {t('ledgerReport.summary', 'ملخص')} {currentLedgerType?.label}
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={2}>
                  <StatCard 
                    label={t('ledgerReport.openingBalance', 'رصيد افتتاحي')} 
                    value={fmt(report.openingBalance)}
                    color="info.main"
                    icon={<TrendingUpIcon />}
                  />
                </Grid>
                
                {isMoneyLedger ? (
                  <>
                    <Grid item xs={12} sm={6} md={2}>
                      <StatCard 
                        label={t('ledgerReport.totalIncoming', 'إجمالي وارد')} 
                        value={fmt(report.totalIn)}
                        color="success.main"
                        icon={<TrendingUpIcon />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <StatCard 
                        label={t('ledgerReport.totalOutgoing', 'إجمالي صادر')} 
                        value={fmt(report.totalOut)}
                        color="error.main"
                        icon={<TrendingDownIcon />}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6} md={2}>
                      <StatCard 
                        label={t('ledgerReport.totalDebit', 'إجمالي مدين')} 
                        value={fmt(report.totalDebit)}
                        color="warning.main"
                        icon={<TrendingUpIcon />}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                      <StatCard 
                        label={t('ledgerReport.totalCredit', 'إجمالي دائن')} 
                        value={fmt(report.totalCredit)}
                        color="success.main"
                        icon={<TrendingDownIcon />}
                      />
                    </Grid>
                  </>
                )}
                
                <Grid item xs={12} sm={12} md={4}>
                  <Paper 
                    elevation={3}
                    sx={{ 
                      p: 3, 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      textAlign: 'center',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      {t('ledgerReport.closingBalance', 'الرصيد الختامي')}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {fmt(report.closingBalance)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Fade>

        {/* جدول البيانات */}
        <Card>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, pb: 0 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ReportIcon />
                {t('ledgerReport.movementDetails', 'تفاصيل الحركات')} ({report.items.length} {t('ledgerReport.movement', 'حركة')})
              </Typography>
            </Box>
            
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map(c => (
                      <TableCell 
                        key={c.key} 
                        align={c.align || 'center'}
                        sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          width: c.width
                        }}
                      >
                        {c.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {report.items.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length} align="center" sx={{ py: 8 }}>
                        <Alert severity="info" sx={{ maxWidth: 400, mx: 'auto' }}>
                          {t('ledgerReport.noMovements', 'لا توجد حركات مالية في الفترة المحددة')}
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )}
                  {report.items.map((row, i) => (
                    <TableRow 
                      key={i} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: 'action.hover' },
                        '&:hover': { backgroundColor: 'action.selected' }
                      }}
                    >
                      {columns.map(c => (
                        <TableCell 
                          key={c.key} 
                          align={c.align || 'center'}
                          sx={{ py: 1.5 }}
                        >
                          {renderCell(c.key, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Container>
    </ThemeProvider>
  </CacheProvider>
  );
}

function StatCard({ label, value, color, icon }) {
  return (
    <Paper 
      elevation={2}
      sx={{ 
        p: 2, 
        textAlign: 'center',
        borderRadius: 2,
        transition: 'transform 0.2s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
        <Box sx={{ color, mr: 1 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', color }}>
        {value}
      </Typography>
    </Paper>
  );
}

